import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { TableSessionsFetchAPI } from "../api/TableSessions";
import {
  connectTableSessionSocket,
  disconnectTableSessionSocket,
  isTableSessionSocketConnected,
  onTableSessionSocketState,
} from "../utils/tableSessionSocket";

const TableSessionContext = createContext(null);

const ACTIVE_STATUSES = ["APPROVED", "ACTIVE"];

// Dispatched when a collaborative call comes back 401 (participant/session cookie invalid or
// expired). App.js listens for this the same way it already listens for AUTH_INVALID_EVENT /
// CONTEXT_INVALID_EVENT (see src/utils/ownershipContext.js) and redirects somewhere safe - this
// context never calls useNavigate()/navigate() itself so it stays usable outside a Route.
export const TABLE_SESSION_INVALID_EVENT = "smartpay:table-session-invalid";
export const TABLE_SESSION_ORDER_FINISHED_EVENT = "smartpay:table-session-order-finished";

// Marca liviana en localStorage (no la cookie httpOnly de la sesion, esa el JS nunca la lee) de que
// ESTE navegador estaba en una sesion de mesa activa. Sin esto, una recarga de pagina despues de que
// el mesero cierra/anula la cuenta no tiene forma de distinguir "nunca hubo mesa" de "SI habia mesa y
// se acaba de cerrar" - el participante ya quedo invalido en el backend (revokeParticipantsAndInvites
// ForSession) apenas la sesion termina, asi que para entonces ya no hay nada que preguntarle al
// servidor. Se limpia sola en cuanto se muestra el aviso o empieza una sesion nueva.
const ACTIVE_TABLE_SESSION_MARKER_KEY = "smartpay_active_table_session_marker";
// Ventana en la que una recarga todavia cuenta como "se acaba de cerrar" y no como una marca vieja
// abandonada (el cliente cerro la pestaña hace dias y hoy vuelve a pedir delivery normal, por ejemplo).
const ACTIVE_TABLE_SESSION_MARKER_MAX_AGE_MS = 6 * 60 * 60 * 1000;

const readActiveTableSessionMarker = () => {
  try {
    const raw = localStorage.getItem(ACTIVE_TABLE_SESSION_MARKER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.at || Date.now() - parsed.at > ACTIVE_TABLE_SESSION_MARKER_MAX_AGE_MS) return null;
    return parsed;
  } catch (err) {
    return null;
  }
};

const writeActiveTableSessionMarker = (sessionUuid) => {
  try {
    localStorage.setItem(ACTIVE_TABLE_SESSION_MARKER_KEY, JSON.stringify({ session_uuid: sessionUuid, at: Date.now() }));
  } catch (err) {
    // Privado/almacenamiento bloqueado - el aviso de cierre simplemente no se muestra en una
    // recarga; no es critico, el resto del flujo de mesa no depende de esto.
  }
};

const clearActiveTableSessionMarker = () => {
  try {
    localStorage.removeItem(ACTIVE_TABLE_SESSION_MARKER_KEY);
  } catch (err) {
    // no-op
  }
};

const getNested = (value, paths) => {
  for (const path of paths) {
    const found = path.split(".").reduce((acc, key) => acc?.[key], value);
    if (found !== undefined && found !== null && found !== "") return found;
  }
  return null;
};

const normalizeBusiness = (data) => {
  const business = data?.business || data?.restaurant || data?.branchoffice?.business || {};
  if (!business || typeof business !== "object") return null;

  return {
    id: business.id ?? business.business_id ?? null,
    name: business.name ?? business.business_name ?? data?.business_name ?? "",
    description: business.description ?? "",
    code: business.code ?? business.business_code ?? data?.business_code ?? "",
    config: null,
    events_enabled: business.events_enabled ?? 0,
  };
};

const normalizeBranch = (data) => {
  const branch = data?.branchoffice || data?.branch || data?.sucursal || {};
  if (!branch || typeof branch !== "object") return null;

  return {
    id: branch.id ?? branch.branchoffice_id ?? branch.branchofficeId ?? null,
    name: branch.name ?? branch.branchofficeName ?? branch.branchoffice_name ?? data?.branchoffice_name ?? "",
    code: branch.code ?? branch.branchoffice_code ?? data?.branchoffice_code ?? "",
    order_printed: branch.printed_order ?? branch.order_printed ?? 0,
    branchoffice_printer: branch.printer_name ?? branch.branchoffice_printer ?? null,
    branchoffice_support_phone: branch.supportPhone ?? branch.branchoffice_support_phone ?? "",
    branchoffice_open: branch.branchoffice_open ?? branch.open ?? 1,
    machine_ecommerce: branch.machine_ecommerce ?? [],
    machine_table: branch.machine_table ?? [],
  };
};

const defaultMachineConfig = {
  mainContainer: true,
  deliveryGroup: { actvPlus: true },
  buttons: {
    groupPlus: true,
    groupLite: false,
    menu: { groupPlus: true, topNav: false, actvLite: false },
    discounts: { actvPlus: true, actvLite: false },
    coupons: { actvPlus: true, actvLite: false },
    pendingOrders: { actvPlus: true, actvLite: false },
    return: { actvPlus: true, actvLite: false },
  },
};

const normalizeMachine = (data) => {
  const branch = data?.branchoffice || data?.branch || {};
  const machine =
    data?.machine ||
    data?.machine_ecommerce ||
    branch?.machine ||
    (Array.isArray(branch?.machine_ecommerce) ? branch.machine_ecommerce[0] : null) ||
    (Array.isArray(data?.machine_ecommerce) ? data.machine_ecommerce[0] : null);

  if (!machine || typeof machine !== "object") return null;

  return {
    id: machine.id ?? machine.machine_per_branch_id ?? machine.branch_machine_id ?? null,
    code: machine.code ?? machine.machine_code ?? "",
    is_logged: machine.is_logged ?? 0,
    name_type_machine: machine.name_type_machine ?? "",
    machine_type_id: machine.machine_type_id ?? 1,
    printers: machine.printers ?? [],
    machine_config: machine.machine_config ?? defaultMachineConfig,
    machine_open: machine.machine_open ?? 1,
  };
};

export const normalizeTableSessionPayload = (payload = {}) => {
  const source = payload?.session || payload?.table_session || payload?.current_session || payload;
  const table = source?.table || source?.mesa || {};
  const environment = source?.environment || source?.ambiente || table?.environment || {};
  const status = source?.status || source?.state || payload?.status || null;

  return {
    ...source,
    status,
    business: normalizeBusiness(source),
    branchoffice: normalizeBranch(source),
    machine: normalizeMachine(source),
    table: {
      id: table.id ?? table.table_id ?? source?.table_id ?? null,
      code: table.code ?? table.table_code ?? source?.table_code ?? "",
      public_code: table.public_code ?? table.public_access_code ?? source?.public_table_code ?? "",
      name: table.name ?? table.table_name ?? source?.table_name ?? source?.name ?? "",
      number: table.number ?? table.table_number ?? source?.table_number ?? "",
      environment: environment.name ?? environment.environment_name ?? table.environment_name ?? source?.environment_name ?? "",
      disabled: Boolean(table.disabled ?? table.is_disabled ?? source?.table_disabled ?? false),
      available: source?.available ?? table.available ?? true,
    },
    businessName: getNested(source, ["business.name", "restaurant.name", "business_name", "restaurant_name"]),
    branchofficeName: getNested(source, ["branchoffice.name", "branch.name", "branchoffice_name", "branch_name"]),
    // Present only right after /request/status picks up an APPROVED request, or right after
    // /invitations/:token/join - { participant_uuid, role, display_name }. Never trust any other
    // participant_uuid than the one this device gets back from the server.
    participant: source?.participant || null,
  };
};

export const applyTableSessionEcommerceContext = (session) => {
  if (!session) return;

  const business = session.business || normalizeBusiness(session);
  const branchoffice = session.branchoffice || normalizeBranch(session);
  const machine = session.machine || normalizeMachine(session);

  if (business?.id && business?.code) {
    localStorage.setItem("business", JSON.stringify(business));
  }

  const businessConfig = session.businessConfig || session.business_config || session.config;
  if (businessConfig) {
    localStorage.setItem("businessConfig", JSON.stringify(businessConfig));
    if (businessConfig.logo) localStorage.setItem("logo", businessConfig.logo);
  } else if (session.logo) {
    localStorage.setItem("logo", session.logo);
  }

  if ("frecuentShopperConfig" in session || "frequentShopperConfig" in session) {
    const shopperConfig = session.frecuentShopperConfig ?? session.frequentShopperConfig;
    localStorage.setItem("frecuentShopperConfig", shopperConfig ? JSON.stringify(shopperConfig) : null);
  }

  if (branchoffice?.id && branchoffice?.code) {
    localStorage.setItem("branchoffice", JSON.stringify(branchoffice));
    localStorage.setItem("branchoffice_original", JSON.stringify(branchoffice));
  }

  if (machine?.id && machine?.code) {
    localStorage.setItem("machine", JSON.stringify(machine));
    localStorage.setItem("machine_original", JSON.stringify(machine));
    localStorage.setItem("parthner_order", false);
  }

  // Deliberately NOT clearing participant identity here - that lives in its own HttpOnly cookie
  // (smartpay_table_participant), never in localStorage (see Fase 3: "No usar localStorage como
  // identidad de participante").
  localStorage.removeItem("table");
  localStorage.removeItem("codeRoom");
};

export const isActiveTableSessionStatus = (status) => ACTIVE_STATUSES.includes(String(status || "").toUpperCase());

const isMissingTableOrderError = (error) => (
  Number(error?.response?.status) === 404 &&
  String(error?.response?.data?.error || error?.response?.data?.message || error?.message || "")
    .toLowerCase()
    .includes("orden de mesa no encontrada")
);

const generateIdempotencyKey = () => {
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  }
  const hex = "0123456789abcdef";
  let key = "";
  for (let i = 0; i < 64; i += 1) key += hex[Math.floor(Math.random() * hex.length)];
  return key;
};

// The public snapshot returned by every shared-order endpoint (GET/POST/PUT /order, and every
// /order/items/* command - see smartpay-dashboard src/app/services/tableSessionOrderSnapshot.js)
// already has is_owner/editable baked in when the backend knows the requesting participant (a
// direct HTTP response always does). The socket broadcast is viewer-agnostic on purpose (one
// payload reaches every device in the room) and always carries is_owner:false/editable:false. This
// re-stamps both fields client-side from whichever line we're looking at against *our own*
// participant_uuid, so a GUEST's own items always render as editable regardless of whether the
// snapshot came from our own HTTP response or a broadcast about someone else's change.
const applyOwnership = (snapshot, participantUuid) => {
  if (!snapshot) return snapshot;

  const stamp = (line) => ({
    ...line,
    is_owner: Boolean(participantUuid) && line.participant_uuid === participantUuid,
    // Mirrors the backend's own editable formula (tableSessionOrderSnapshot.js#sanitizeLine): tracks
    // kitchen_status/payment_status, not line_status - the owner can still amend/remove a line after
    // it's been submitted, right up until a waiter sends it to the kitchen from the POS, or until
    // someone claims/pays it (Pre-pago partial payment - see smartpay-dashboard
    // services/tableSessionPayments.js). payment_status defaults to UNPAID for any snapshot from
    // before that feature existed, so this is a no-op there.
    editable: Boolean(participantUuid)
      && line.participant_uuid === participantUuid
      && line.kitchen_status !== "COOKING"
      && (line.payment_status || "UNPAID") === "UNPAID",
    // Mismo criterio que tableSessionOrderSnapshot.js#sanitizeLine (el broadcast viaja sin viewer): dueño,
    // aun sin pagar y sin una solicitud de descuento en espera o aprobada (una rechazada se puede repetir).
    can_request_discount: Boolean(participantUuid)
      && line.participant_uuid === participantUuid
      && (line.payment_status || "UNPAID") === "UNPAID"
      && line.line_status !== "CANCELLED"
      && (!line.discount_request || line.discount_request.status === "REJECTED"),
  });

  return {
    ...snapshot,
    groups: Array.isArray(snapshot.groups) ? snapshot.groups.map(stamp) : [],
    combos: Array.isArray(snapshot.combos) ? snapshot.combos.map(stamp) : [],
  };
};

export const TableSessionProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);

  // Fase 3 collaborative state.
  const [participant, setParticipant] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [participantLoading, setParticipantLoading] = useState(false);
  const [currentInvite, setCurrentInvite] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const [sharedOrder, setSharedOrder] = useState(null);
  const [sharedOrderVersion, setSharedOrderVersion] = useState(0);
  const [sharedOrderLoading, setSharedOrderLoading] = useState(false);
  const [sharedOrderError, setSharedOrderError] = useState(null);
  const [socketConnected, setSocketConnected] = useState(false);
  const [lastMutationError, setLastMutationError] = useState(null);
  const [lastItemsSubmittedEvent, setLastItemsSubmittedEvent] = useState(null);
  // Ultima resolucion (aprobada/rechazada) de un descuento que ESTE participante pidio - la UI la usa para
  // avisar con un toast; el cambio de la linea en si llega por tableSessionSharedOrderUpdated.
  const [lastDiscountResolvedEvent, setLastDiscountResolvedEvent] = useState(null);
  // Se activa cuando una sesion de mesa que SI estaba activa (el cliente estaba a mitad de un pedido)
  // termina - ya sea porque el propio cliente termino de pagar, o porque el mesero cerro/anulo la
  // cuenta desde el POS. Sin esto, refreshCurrentSession's has_table_session:false simplemente vacia
  // el estado y Checkout.jsx cae de vuelta al carrito normal (vacio, sin relacion con la mesa) sin
  // ninguna explicacion - ver el aviso "tu cuenta se cerro" que arma con esto.
  const [closedSessionNotice, setClosedSessionNotice] = useState(null);
  const dismissClosedSessionNotice = useCallback(() => setClosedSessionNotice(null), []);

  const sessionUuidRef = useRef(null);
  const sharedOrderVersionRef = useRef(0);
  const participantRef = useRef(null);
  // Read by addSharedItem below to decide whether to reset a pending "solicitar pagar" - a plain
  // useCallback closing over `session` directly would go stale (deps stay [runCollaborativeMutation]).
  const sessionRef = useRef(null);
  const activeTempOrderCodeRef = useRef(null);
  // "Cuentas separadas" is opt-in per table (see split_accounts_enabled on the backend session
  // model, default false). Every "cuenta compartida" participant has account_uuid===null forever,
  // while the shared order's own account_uuid gets lazily backfilled to a real UUID the first time
  // anyone touches it (ensureAccountFields on the backend) - so a raw account_uuid equality check
  // would wrongly drop every real-time event once that backfill happens. Only enforce the
  // per-account match below when the table actually has multiple accounts in play.
  const splitAccountsEnabledRef = useRef(false);

  useEffect(() => { sessionUuidRef.current = session?.session_uuid || null; }, [session?.session_uuid]);
  useEffect(() => { sharedOrderVersionRef.current = sharedOrderVersion; }, [sharedOrderVersion]);
  useEffect(() => { participantRef.current = participant; }, [participant]);
  useEffect(() => { sessionRef.current = session; }, [session]);
  useEffect(() => { splitAccountsEnabledRef.current = Boolean(session?.split_accounts_enabled); }, [session?.split_accounts_enabled]);
  // Espejo de `status` para refreshCurrentSession (deps [clearSessionState] fijas, no puede leer
  // `status` directo sin quedar obsoleto/recrearse en cada cambio) - solo se usa para distinguir "no
  // hay sesion" de "HABIA sesion activa y se acaba de cerrar".
  const wasActiveStatusRef = useRef(false);
  useEffect(() => { wasActiveStatusRef.current = isActiveTableSessionStatus(status); }, [status]);
  useEffect(() => {
    activeTempOrderCodeRef.current = session?.active_temp_order_code
      || sharedOrder?.order_code
      || sharedOrder?.code
      || null;
  }, [session?.active_temp_order_code, sharedOrder?.order_code, sharedOrder?.code]);

  const getMachineSocketQuery = useCallback(() => {
    const storedMachine = typeof localStorage !== "undefined"
      ? JSON.parse(localStorage.getItem("machine") || "null")
      : null;
    const storedBranch = typeof localStorage !== "undefined"
      ? JSON.parse(localStorage.getItem("branchoffice") || "null")
      : null;
    const storedBusiness = typeof localStorage !== "undefined"
      ? JSON.parse(localStorage.getItem("business") || "null")
      : null;
    return {
      machineCode: session?.machine?.code || storedMachine?.code,
      branchofficeCode: session?.branchoffice?.code || storedBranch?.code,
      businessCode: session?.business?.code || storedBusiness?.code,
    };
  }, [session?.machine?.code, session?.branchoffice?.code, session?.business?.code]);

  const clearSessionState = useCallback(() => {
    setSession(null);
    setStatus(null);
    setError(null);
    setParticipant(null);
    setParticipants([]);
    setCurrentInvite(null);
    setInviteError(null);
    setSharedOrder(null);
    setSharedOrderVersion(0);
    setSharedOrderError(null);
    setLastMutationError(null);
  }, []);

  const applySnapshot = useCallback((rawSnapshot) => {
    if (!rawSnapshot) {
      setSharedOrder(null);
      setSharedOrderVersion(0);
      return null;
    }

    const stamped = applyOwnership(rawSnapshot, participantRef.current?.participant_uuid || null);
    setSharedOrder(stamped);
    setSharedOrderVersion(Number(stamped.version) || 0);
    return stamped;
  }, []);

  const refreshSharedOrder = useCallback(async () => {
    setSharedOrderLoading(true);
    try {
      const raw = await TableSessionsFetchAPI.getActiveOrder();
      const stamped = applySnapshot(raw);
      setSharedOrderError(null);
      return stamped;
    } catch (err) {
      setSharedOrderError(err);
      return null;
    } finally {
      setSharedOrderLoading(false);
    }
  }, [applySnapshot]);

  // Fase 11 - single choke point for every collaborative HTTP error. Never silently swallows an
  // error (callers still get the rejected promise so Checkout.jsx can revert its own optimistic
  // update), but it does react to the specific status/code pairs the backend actually returns:
  //   401 -> the participant/session cookie is dead. Clear local state and let App.js redirect.
  //   409 ORDER_VERSION_CONFLICT / SESSION_NOT_ACTIVE / NOTHING_TO_SUBMIT -> our view is stale or
  //     the command no longer makes sense; one recovery GET, never a loop of retries.
  //   404 (line/order not found) -> the order changed under us; resync once.
  //   403 (LINE_NOT_OWNED / PARTICIPANT_REQUIRED / PRIMARY_ROLE_REQUIRED) -> no resync needed, just
  //     surfaced via lastMutationError for the UI to show inline.
  const handleMutationError = useCallback((err) => {
    const httpStatus = err?.response?.status;
    const data = err?.response?.data;
    const code = data?.code;
    const message = data?.error || err?.message || "No fue posible completar la accion.";

    setLastMutationError({ status: httpStatus, code, message, at: Date.now() });

    if (httpStatus === 401) {
      clearSessionState();
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(TABLE_SESSION_INVALID_EVENT, { detail: { code } }));
      }
      return;
    }

    if (httpStatus === 409 || (httpStatus === 404 && code !== "SESSION_NOT_ACTIVE")) {
      refreshSharedOrder();
    }
  }, [clearSessionState, refreshSharedOrder]);

  // `execute(version, isRetry)` builds the request for a given expected_version instead of a version
  // baked in beforehand - that's what lets a genuine collision (two participants' mutations landing in
  // the same instant, each targeting the version they last saw) retry against the REAL current version
  // instead of blindly resending the exact same stale one, which would just 409 again. Exactly one
  // retry, same "never a loop of retries" policy as handleMutationError below: a garden-variety
  // collision resolves invisibly to the customer; a table under constant, unresolvable contention still
  // surfaces the same error as before this existed.
  const runCollaborativeMutation = useCallback(async (execute) => {
    const attempt = async (version, isRetry) => {
      try {
        const raw = await execute(version, isRetry);
        const stamped = applySnapshot(raw);
        setLastMutationError(null);
        return stamped;
      } catch (err) {
        if (!isRetry && err?.response?.data?.code === "ORDER_VERSION_CONFLICT") {
          const refreshed = await refreshSharedOrder();
          return attempt(Number(refreshed?.version) || undefined, true);
        }
        handleMutationError(err);
        throw err;
      }
    };
    return attempt(sharedOrderVersionRef.current || undefined, false);
  }, [applySnapshot, handleMutationError, refreshSharedOrder]);

  const refreshActiveTableOrder = useCallback(async () => {
    if (!isActiveTableSessionStatus(status)) {
      setSharedOrder(null);
      setSharedOrderVersion(0);
      return null;
    }
    return refreshSharedOrder();
  }, [status, refreshSharedOrder]);

  // Legacy full-replace submit (Fase 13 compatibility layer). Untouched behaviourally from before
  // this feature existed: still does GET-existing -> PUT or POST full cart -> re-fetch. The backend
  // now refuses this path with COLLABORATIVE_ORDER_LOCKED as soon as any line has a participant_uuid
  // (see smartpay-dashboard services/tableSessions.js#createOrUpdateOrder), so it naturally stops
  // being usable the moment a table goes collaborative - callers should prefer addSharedItem/
  // updateSharedItem/removeSharedItem/submitMyItems for any new table-mode UI.
  const submitTableOrder = useCallback(async ({ cart, idempotencyKey }) => {
    if (!isActiveTableSessionStatus(status)) {
      throw new Error("No existe una sesion de mesa activa.");
    }

    setSharedOrderLoading(true);
    try {
      const activeOrder = await TableSessionsFetchAPI.getActiveOrder();
      applySnapshot(activeOrder);

      let response;
      if (activeOrder) {
        try {
          response = await TableSessionsFetchAPI.updateTableOrder(cart, idempotencyKey);
        } catch (err) {
          if (!isMissingTableOrderError(err)) throw err;
          response = await TableSessionsFetchAPI.createTableOrder(cart, idempotencyKey);
        }
      } else {
        response = await TableSessionsFetchAPI.createTableOrder(cart, idempotencyKey);
      }

      const stamped = applySnapshot(response);
      setSharedOrderError(null);

      const refreshed = await TableSessionsFetchAPI.getActiveOrder();
      const refreshedStamped = applySnapshot(refreshed);
      return refreshedStamped || stamped;
    } catch (err) {
      setSharedOrderError(err);
      throw err;
    } finally {
      setSharedOrderLoading(false);
    }
  }, [status, applySnapshot]);

  // silent: refresco en segundo plano (reconexion del socket / volver a la pantalla) - no prende el
  // indicador de carga global para no parpadear la pantalla del cliente por un refresco que no pidio.
  const refreshCurrentSession = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const current = await TableSessionsFetchAPI.getCurrentSession();
      if (current?.has_table_session === false) {
        // Dos formas de saber que SI habia una mesa activa (para no mostrar este aviso a un cliente
        // que nunca tuvo mesa, que tambien pasa por aqui en cada carga normal de la app):
        //   1. wasActiveStatusRef - la pestaña misma vio la sesion activa antes de este refresh (el
        //      cierre en vivo llego por socket mientras el cliente seguia con la app abierta).
        //   2. el marcador de localStorage - la pagina se acaba de RECARGAR, asi que la memoria de
        //      React de arriba no sirve, pero el marcador que se escribio al entrar a la mesa si.
        if (wasActiveStatusRef.current || readActiveTableSessionMarker()) {
          setClosedSessionNotice({ at: Date.now() });
        }
        clearActiveTableSessionMarker();
        clearSessionState();
        return null;
      }

      const normalized = normalizeTableSessionPayload(current);
      setSession(normalized);
      setStatus(normalized.status);
      setError(null);

      if (normalized.participant) {
        setParticipant(normalized.participant);
        setParticipants((prev) => {
          if (prev.some((p) => p.participant_uuid === normalized.participant.participant_uuid)) return prev;
          return [...prev, { ...normalized.participant, status: "ACTIVE" }];
        });
      }

      if (isActiveTableSessionStatus(normalized.status)) {
        // Una sesion nueva (otra mesa, u otra visita) empieza limpia de cualquier aviso viejo.
        setClosedSessionNotice(null);
        writeActiveTableSessionMarker(normalized.session_uuid);
        applyTableSessionEcommerceContext(normalized);
      }

      return normalized;
    } catch (err) {
      // Un refresco en segundo plano que falla (tipico al volver de segundo plano con la red todavia
      // despertando) NO debe borrar la sesion que el cliente ya tiene: se conserva lo que se sabia y el
      // siguiente evento/refresco lo corrige.
      if (silent) return null;
      clearSessionState();
      setError(err);
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  }, [clearSessionState]);

  const requestTableAccess = useCallback(async ({ publicTableCode, source, customer }) => {
    setError(null);
    const response = await TableSessionsFetchAPI.requestAccess({ publicTableCode, source, customer });
    const normalized = normalizeTableSessionPayload(response);
    setSession(normalized);
    setStatus(normalized.status || "PENDING");
    return response;
  }, []);

  // Customer-initiated "solicitar pagar" - notifies staff, does not itself unlock the Pagar button
  // (payment_authorized stays waiter-controlled - see handlePaymentAuthorized below, which this
  // shares a shape with: both just need to reread the sanitized session snapshot).
  const requestPayment = useCallback(async () => {
    const response = await TableSessionsFetchAPI.requestPayment();
    await refreshCurrentSession();
    return response;
  }, [refreshCurrentSession]);

  const exitTableMode = useCallback(async () => {
    try {
      await TableSessionsFetchAPI.exitCurrentSession();
    } catch (err) {
      setError(err);
    } finally {
      disconnectTableSessionSocket();
      clearSessionState();
      await refreshCurrentSession();
    }
  }, [clearSessionState, refreshCurrentSession]);

  // ---- Fase 5/6 invitations ----

  const createSessionInvite = useCallback(async () => {
    setInviteLoading(true);
    setInviteError(null);
    try {
      const invite = await TableSessionsFetchAPI.createInvite();
      setCurrentInvite(invite);
      return invite;
    } catch (err) {
      setInviteError(err?.response?.data?.error || err.message);
      throw err;
    } finally {
      setInviteLoading(false);
    }
  }, []);

  const revokeSessionInvite = useCallback(async (inviteUuid) => {
    const target = inviteUuid || currentInvite?.invite_uuid;
    if (!target) return;

    setInviteLoading(true);
    try {
      await TableSessionsFetchAPI.revokeInvite(target);
      setCurrentInvite((prev) => (prev && prev.invite_uuid === target ? null : prev));
    } catch (err) {
      setInviteError(err?.response?.data?.error || err.message);
      throw err;
    } finally {
      setInviteLoading(false);
    }
  }, [currentInvite]);

  // Unauthenticated preview - safe to call before the guest has any cookie at all.
  const validateSessionInvite = useCallback((token) => TableSessionsFetchAPI.getInvite(token), []);

  // Consumes the invite (mints the guest's own participant cookie server-side), then refreshes this
  // device's own session/participant identity from that new cookie.
  const joinSessionWithInvite = useCallback(async (token, displayName) => {
    const result = await TableSessionsFetchAPI.joinInvite(token, displayName);
    await refreshCurrentSession();
    return result;
  }, [refreshCurrentSession]);

  // No public "list participants" endpoint exists yet on the backend (see smartpay-dashboard
  // src/app/routes/tableSessions.js - only the POS/staff-auth GET /table-sessions/table/:id/active
  // returns a full roster). This recomputes locally from what this device already knows: itself,
  // whoever it has seen join live via tableSessionParticipantJoined, folded with
  // participants_summary from the shared-order snapshot (item counts) when available. It is NOT a
  // network call - replace the body with a real fetch once a public roster endpoint ships.
  const refreshParticipants = useCallback(() => {
    setParticipantLoading(true);
    setParticipants((prev) => {
      const summary = sharedOrder?.participants_summary || [];
      const byUuid = new Map(prev.map((p) => [p.participant_uuid, p]));
      summary.forEach((entry) => {
        const existing = byUuid.get(entry.participant_uuid) || {};
        byUuid.set(entry.participant_uuid, { ...existing, ...entry });
      });
      return Array.from(byUuid.values());
    });
    setParticipantLoading(false);
  }, [sharedOrder]);

  // ---- Fase 6/9 shared-order item commands ----
  // expected_version is always the last version this device has seen (sharedOrderVersionRef);
  // omitted on the very first command of a session (ref is 0, nothing to compare against yet).

  const submitMyItems = useCallback((lineUuids, idempotencyKey) => (
    // Each attempt needs its own idempotency key: retrying with a fresh version (see
    // runCollaborativeMutation) is legitimately a different payload, so reusing the caller's key would
    // hit the idempotency layer's "same key, different payload" rejection instead of actually retrying.
    runCollaborativeMutation((version, isRetry) => TableSessionsFetchAPI.submitSharedItems(
      lineUuids, version, (isRetry || !idempotencyKey) ? generateIdempotencyKey() : idempotencyKey
    ))
  ), [runCollaborativeMutation]);

  const addSharedItem = useCallback(async (item, idempotencyKey) => {
    const type = item?.combo_id ? "combo" : "group";
    const wasPaymentRequested = Boolean(sessionRef.current?.payment_requested);
    const result = await runCollaborativeMutation((version, isRetry) => TableSessionsFetchAPI.addSharedItem(
      { type, item, expectedVersion: version },
      (isRetry || !idempotencyKey) ? generateIdempotencyKey() : idempotencyKey
    ));
    // Still ordering cancels a pending "solicitar pagar" - the backend already reset
    // payment.requested as part of this same addItem call (see requestTablePayment/addItem on the
    // backend); refetch so Checkout.jsx's request-payment button reappears instead of staying
    // stuck on "esperando al mesero" from stale session state.
    if (wasPaymentRequested) refreshCurrentSession();

    // Adding an item already means the customer wants it, and the backend now creates the line
    // straight as SUBMITTED in that same request (see addItem on the backend) - there's no separate
    // DRAFT-then-submit round trip to make here anymore. That used to be two sequential writes racing
    // the same optimistic-concurrency version guard: any concurrent mutation landing between them
    // (another participant, a waiter action) could reject the second write and leave the line stuck
    // DRAFT - added, but with no checkbox to pay it, and nothing telling the customer why. Collapsing
    // it into the one atomic backend write removes that race window entirely instead of retrying
    // around it.
    return result;
  }, [runCollaborativeMutation, refreshCurrentSession]);

  const updateSharedItem = useCallback((lineUuid, changes, idempotencyKey) => (
    runCollaborativeMutation((version, isRetry) => TableSessionsFetchAPI.updateSharedItem(
      lineUuid, changes, version, (isRetry || !idempotencyKey) ? generateIdempotencyKey() : idempotencyKey
    ))
  ), [runCollaborativeMutation]);

  // Pide un descuento sobre una de MIS lineas (ej. jubilado); el personal lo aprueba o rechaza en el POS.
  const requestItemDiscount = useCallback((lineUuid, reason, idempotencyKey) => (
    runCollaborativeMutation((version, isRetry) => TableSessionsFetchAPI.requestItemDiscount(
      lineUuid, reason, version, (isRetry || !idempotencyKey) ? generateIdempotencyKey() : idempotencyKey
    ))
  ), [runCollaborativeMutation]);

  const removeSharedItem = useCallback((lineUuid, idempotencyKey) => (
    runCollaborativeMutation((version, isRetry) => TableSessionsFetchAPI.removeSharedItem(
      lineUuid, version, (isRetry || !idempotencyKey) ? generateIdempotencyKey() : idempotencyKey
    ))
  ), [runCollaborativeMutation]);

  // ---- Pre-pago partial payment (Fase 4: any participant can claim/pay ANY line, not just their
  // own - a deliberately separate gate from addSharedItem/updateSharedItem/removeSharedItem above). ----

  // Unlike the other collaborative commands, the response wraps the snapshot under .order alongside
  // the payment_attempt_id/calculation totals the caller needs before opening a gateway - so this
  // can't reuse runCollaborativeMutation unchanged (that helper assumes the raw response IS the
  // snapshot).
  const claimPaymentLines = useCallback(async (lineUuids) => {
    try {
      const result = await TableSessionsFetchAPI.claimPrepayLines(lineUuids);
      applySnapshot(result?.order);
      setLastMutationError(null);
      return result;
    } catch (err) {
      handleMutationError(err);
      throw err;
    }
  }, [applySnapshot, handleMutationError]);

  const confirmPaymentClaim = useCallback((paymentAttemptId, paymentDetails) => (
    runCollaborativeMutation(() => TableSessionsFetchAPI.confirmPrepayClaim(paymentAttemptId, paymentDetails))
  ), [runCollaborativeMutation]);

  const releasePaymentClaim = useCallback((paymentAttemptId, reason) => (
    runCollaborativeMutation(() => TableSessionsFetchAPI.releasePrepayClaim(paymentAttemptId, reason))
  ), [runCollaborativeMutation]);

  // "Finalizar cuenta": succeeds (creates the final invoice, closes the session) only once nothing is
  // left owed. There is no new shared-order snapshot to apply on success - the TempOrder is gone and
  // the session is about to close via tableSessionClosed/refreshCurrentSession, same as any other
  // checkout. On a 409 PENDING_BALANCE, the caller should read
  // err.response.data.{pending_balance,unpaid_line_uuids} to route the customer to pay the rest,
  // never retry this blindly.
  const finalizeAccount = useCallback(async () => {
    try {
      const result = await TableSessionsFetchAPI.finalizePrepayAccount();
      setLastMutationError(null);
      return result;
    } catch (err) {
      handleMutationError(err);
      throw err;
    }
  }, [handleMutationError]);

  // "Personalizar factura": guarda nombre/RUC/DV en la orden de la mesa. La respuesta trae el snapshot ya
  // con invoice_data, asi que se aplica de una vez (el eco del socket trae la misma version y se ignora).
  const setInvoiceData = useCallback(async (invoiceData) => {
    try {
      const result = await TableSessionsFetchAPI.setInvoiceData(invoiceData);
      if (result?.order) applySnapshot(result.order);
      setLastMutationError(null);
      return result;
    } catch (err) {
      handleMutationError(err);
      throw err;
    }
  }, [applySnapshot, handleMutationError]);

  // Para los listeners del socket/visibilidad de abajo, que no deben reabrirse cada vez que cambie la
  // identidad de refreshCurrentSession.
  const refreshCurrentSessionRef = useRef(null);
  refreshCurrentSessionRef.current = refreshCurrentSession;

  useEffect(() => {
    refreshCurrentSession();
  }, [refreshCurrentSession]);

  // Fase 12: exactly one GET when a session first becomes APPROVED/ACTIVE for this device (covers
  // both "just approved" and "recovered an existing session via cookie on page load"). No polling.
  useEffect(() => {
    if (!isActiveTableSessionStatus(status)) return;
    refreshSharedOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, session?.session_uuid]);

  // Fase 2: connect/disconnect the single socket exactly when this device's session is (or stops
  // being) APPROVED/ACTIVE - the only window during which the backend's cookie-based room join
  // (src/sockets.js#joinTableSessionRoom on smartpay-dashboard) can actually succeed.
  // session_uuid con el que se abrio el socket que esta vivo ahora mismo (ver el efecto de abajo).
  const socketSessionUuidRef = useRef(null);

  useEffect(() => {
    if (!isActiveTableSessionStatus(status)) {
      disconnectTableSessionSocket();
      socketSessionUuidRef.current = null;
      setSocketConnected(false);
      return undefined;
    }

    // El servidor mete al socket al room de SU sesion unicamente en el momento de conectarse, leyendo la
    // cookie del handshake - nunca despues. Si esta pestaña ya tenia el socket abierto de una sesion
    // anterior (o de antes de que existiera la nueva) y ahora entra a OTRA sesion (otro QR escaneado en la
    // misma pestaña/telefono, invitacion a otra mesa...), el estado sigue siendo "activo", asi que este
    // efecto no volvia a correr y el socket se quedaba SIN room de la sesion nueva: ese celular dejaba de
    // recibir en vivo todo (mover de mesa, pagos, cambios de la cuenta, factura...). Reabrirlo cuando
    // cambia el session_uuid fuerza un handshake nuevo con la cookie nueva.
    const currentSessionUuid = session?.session_uuid || null;
    if (socketSessionUuidRef.current && socketSessionUuidRef.current !== currentSessionUuid) {
      disconnectTableSessionSocket();
      setSocketConnected(false);
    }
    connectTableSessionSocket(getMachineSocketQuery());
    socketSessionUuidRef.current = currentSessionUuid;

    // Un room de socket.io no reproduce eventos pasados: si el celular estaba con la pestaña en segundo
    // plano o reconectando justo cuando el POS movio la mesa (tableSessionMoved), cerro/edito la cuenta,
    // etc., ese aviso se pierde y el cliente seguia viendo datos viejos (ej. "Mesa 23" aunque ya estaba
    // en la 16). Cada vez que el socket (re)conecta o la pantalla vuelve a ser visible se vuelve a pedir
    // la sesion (barato, un GET; mismo camino que usan los eventos en vivo). Con tope de 5s entre
    // refrescos para no encadenar un GET por cada evento de visibilidad/reconexion seguido.
    let lastResyncAt = 0;
    const resyncSession = () => {
      const now = Date.now();
      if (now - lastResyncAt < 5000) return;
      lastResyncAt = now;
      refreshCurrentSessionRef.current?.({ silent: true });
    };

    // En socket.io v4 una reconexion vuelve a disparar "connect" (el "reconnect" del helper no llega a
    // este socket) - el primer connect es la carga normal, los siguientes son reconexiones reales.
    let hasConnectedBefore = Boolean(isTableSessionSocketConnected());
    const unsubscribe = onTableSessionSocketState((state) => {
      if (state === "connect" || state === "reconnect") setSocketConnected(true);
      if (state === "connect") {
        if (hasConnectedBefore) resyncSession();
        hasConnectedBefore = true;
      }
      if (state === "reconnect") resyncSession();
      if (state === "disconnect" || state === "connect_error") setSocketConnected(false);
    });
    const handleVisibility = () => {
      if (document.visibilityState === "visible") resyncSession();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", handleVisibility);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", handleVisibility);
      unsubscribe();
    };
  }, [status, session?.session_uuid, getMachineSocketQuery]);

  // Provider unmount (app teardown only, in practice never during normal navigation since this
  // wraps the whole router in App.js) - always release the connection.
  useEffect(() => () => {
    disconnectTableSessionSocket();
  }, []);

  // Fase 4: every collaborative socket event, registered once per session_uuid (not per render, not
  // per sharedOrderVersion change) and torn down with the exact same handler references.
  useEffect(() => {
    if (!isActiveTableSessionStatus(status) || !session?.session_uuid) return undefined;

    const socket = connectTableSessionSocket(getMachineSocketQuery());
    const belongsToSession = (payload) => Boolean(payload) && payload.session_uuid === sessionUuidRef.current;

    // "Cuentas separadas": tableSessionSharedOrderUpdated broadcasts to the WHOLE session room
    // regardless of which account changed (every account's TempOrder shares one table_session_uuid),
    // so a customer on their own split account must ignore broadcasts about a sibling account's
    // order. null on both sides (pre-feature/default-account order and participant) matches, same
    // convention used everywhere else in this feature. Skipped entirely in "cuenta compartida" mode
    // (split_accounts_enabled false) - see splitAccountsEnabledRef above.
    const belongsToMyAccount = (order) => (
      !splitAccountsEnabledRef.current
      || (order?.account_uuid || null) === (participantRef.current?.account_uuid || null)
    );

    const handleSharedOrderUpdated = (payload) => {
      if (!belongsToSession(payload) || !belongsToMyAccount(payload.order)) return;
      const incomingVersion = Number(payload.version) || 0;
      if (incomingVersion <= sharedOrderVersionRef.current) return; // stale, or our own echo already applied via the HTTP response
      const expectedNext = sharedOrderVersionRef.current + 1;
      if (sharedOrderVersionRef.current > 0 && incomingVersion > expectedNext) {
        // Unexpected version jump (missed an event) - single recovery GET instead of trusting a gap.
        refreshSharedOrder();
        return;
      }
      applySnapshot(payload.order);
    };

    // Emitted once right after this socket (re)connects, if the session has an active order - the
    // reconnect-resync signal: compare, and only GET if the server is actually ahead of us.
    const handleStateVersion = (payload) => {
      if (!belongsToSession(payload)) return;
      const serverVersion = Number(payload.version) || 0;
      if (serverVersion > sharedOrderVersionRef.current) refreshSharedOrder();
    };

    const handleParticipantJoined = (payload) => {
      // "Cuentas separadas": a guest joining a sibling account isn't part of my own bill/roster.
      // Skipped in "cuenta compartida" mode - see splitAccountsEnabledRef above.
      if (!belongsToSession(payload) || (splitAccountsEnabledRef.current && (payload.account_uuid || null) !== (participantRef.current?.account_uuid || null))) return;
      setParticipants((prev) => {
        if (prev.some((p) => p.participant_uuid === payload.participant_uuid)) return prev;
        return [...prev, {
          participant_uuid: payload.participant_uuid,
          role: payload.role,
          display_name: payload.display_name || "",
          status: "ACTIVE",
        }];
      });
    };

    // Not emitted by the backend yet (no "leave"/"remove participant" endpoint exists today - see
    // report). Wired up so the UI already reacts correctly the moment that ships.
    const handleParticipantLeftOrRemoved = (payload) => {
      if (!belongsToSession(payload)) return;
      setParticipants((prev) => prev.filter((p) => p.participant_uuid !== payload.participant_uuid));
    };

    const handleInviteConsumed = (payload) => {
      setCurrentInvite((prev) => (prev && prev.invite_uuid === payload?.invite_uuid ? null : prev));
    };

    const handleInviteRevoked = (payload) => {
      setCurrentInvite((prev) => (prev && prev.invite_uuid === payload?.invite_uuid ? null : prev));
    };

    // The actual data change already arrives via tableSessionSharedOrderUpdated; this event exists
    // so UI can show a "fulano envio sus productos" notice without guessing from line_status diffs.
    const handleItemsSubmitted = (payload) => {
      // "Cuentas separadas": only show "fulano envio sus productos" for my own account's activity.
      // Skipped in "cuenta compartida" mode - see splitAccountsEnabledRef above.
      if (!belongsToSession(payload) || (splitAccountsEnabledRef.current && (payload.account_uuid || null) !== (participantRef.current?.account_uuid || null))) return;
      setLastItemsSubmittedEvent({
        participant_uuid: payload.participant_uuid,
        submitted_count: payload.submitted_count,
        at: Date.now(),
      });
    };

    // El personal resolvio un descuento: solo interesa a quien lo pidio (y en su propia cuenta).
    const handleDiscountResolved = (payload) => {
      if (!belongsToSession(payload)) return;
      if (!participantRef.current?.participant_uuid || payload.participant_uuid !== participantRef.current.participant_uuid) return;
      setLastDiscountResolvedEvent({
        line_uuid: payload.line_uuid,
        status: payload.status,
        discount_name: payload.discount_name,
        rate: payload.rate,
        rejection_note: payload.rejection_note || "",
        at: Date.now(),
      });
    };

    // The final payment flow emits finishedOrder_<temporary-code> to the machine room. The
    // event name itself is the first correlation check; the reference repeats that temporary
    // code before we navigate to the immutable final order. Other orders in the same machine
    // room are therefore ignored.
    const handleAnySocketEvent = (eventName, payload) => {
      const temporaryCode = activeTempOrderCodeRef.current;
      if (!temporaryCode || eventName !== `finishedOrder_${temporaryCode}`) return;

      const reference = payload?.finished_order_reference;
      const referenceTemporaryCode = reference?.temporary_order_code
        || reference?.temp_order_code
        || reference?.reference_order_code
        || reference?.temporary_code
        || payload?.temporary_order_code
        || payload?.temp_order_code;

      // If backend includes the temporary code in the payload, it must match too. Older
      // emitters can rely on the already-matched dynamic event name until they add the field.
      if (referenceTemporaryCode && String(referenceTemporaryCode) !== String(temporaryCode)) return;

      const finalOrderCode = reference?.code
        || reference?.order_code
        || reference?.final_order_code
        || payload?.order_code
        || payload?.final_order_code;
      if (!finalOrderCode || typeof window === "undefined") return;

      window.dispatchEvent(new CustomEvent(TABLE_SESSION_ORDER_FINISHED_EVENT, {
        detail: {
          orderCode: finalOrderCode,
          transactionCode: reference?.transaction_code || reference?.transactionCode || payload?.transaction_code,
          temporaryOrderCode: temporaryCode,
        },
      }));
    };

    const handleSessionTerminal = () => {
      refreshCurrentSession();
    };

    // Waiter-controlled payment gate (POS "Mesas"/"Precuentas"): re-fetch the session so its
    // payment_authorized flag (see sanitizePublicSession on the backend) flips live, without the
    // customer needing to reload the page while the waiter enables their Pagar button.
    const handlePaymentAuthorized = () => {
      refreshCurrentSession();
    };

    // "Cuentas separadas": fires when ONE account finishes (paid/closed) while the table session
    // stays open for the others (see closeSessionForTempOrder on the backend) - the whole-session
    // tableSessionClosed/Expired/Cancelled events above never fire in that case. Only refresh when
    // it's actually this device's own account; a sibling account closing must not disturb this
    // customer's still-active one.
    const handleAccountClosed = (payload) => {
      if (!payload?.account_uuid || payload.account_uuid !== (participantRef.current?.account_uuid || null)) return;
      refreshCurrentSession();
    };

    socket.on("tableSessionSharedOrderUpdated", handleSharedOrderUpdated);
    socket.on("tableSessionAccountClosed", handleAccountClosed);
    socket.on("tableSessionStateVersion", handleStateVersion);
    socket.on("tableSessionParticipantJoined", handleParticipantJoined);
    socket.on("tableSessionParticipantLeft", handleParticipantLeftOrRemoved);
    socket.on("tableSessionParticipantRemoved", handleParticipantLeftOrRemoved);
    socket.on("tableSessionInviteConsumed", handleInviteConsumed);
    socket.on("tableSessionInviteRevoked", handleInviteRevoked);
    socket.on("tableSessionItemsSubmitted", handleItemsSubmitted);
    socket.on("tableSessionDiscountResolved", handleDiscountResolved);
    socket.on("tableSessionClosed", handleSessionTerminal);
    socket.on("tableSessionExpired", handleSessionTerminal);
    socket.on("tableSessionCancelled", handleSessionTerminal);
    socket.on("tableSessionCanceled", handleSessionTerminal);
    socket.on("tableSessionPaymentAuthorized", handlePaymentAuthorized);
    // Another participant at this same table requested payment - re-fetch so payment_requested
    // flips live for everyone at the table, not just the device that clicked the button.
    socket.on("tableSessionPaymentRequested", handlePaymentAuthorized);
    // El cajero movio la sesion a otra mesa (POS "Mover de mesa"): re-pedir la sesion para que el
    // numero de mesa que ve el cliente cambie en vivo. El session_uuid y las cookies no cambian.
    socket.on("tableSessionMoved", handlePaymentAuthorized);
    socket.onAny(handleAnySocketEvent);

    return () => {
      socket.off("tableSessionSharedOrderUpdated", handleSharedOrderUpdated);
      socket.off("tableSessionAccountClosed", handleAccountClosed);
      socket.off("tableSessionStateVersion", handleStateVersion);
      socket.off("tableSessionParticipantJoined", handleParticipantJoined);
      socket.off("tableSessionParticipantLeft", handleParticipantLeftOrRemoved);
      socket.off("tableSessionParticipantRemoved", handleParticipantLeftOrRemoved);
      socket.off("tableSessionInviteConsumed", handleInviteConsumed);
      socket.off("tableSessionInviteRevoked", handleInviteRevoked);
      socket.off("tableSessionItemsSubmitted", handleItemsSubmitted);
      socket.off("tableSessionDiscountResolved", handleDiscountResolved);
      socket.off("tableSessionClosed", handleSessionTerminal);
      socket.off("tableSessionExpired", handleSessionTerminal);
      socket.off("tableSessionCancelled", handleSessionTerminal);
      socket.off("tableSessionCanceled", handleSessionTerminal);
      socket.off("tableSessionPaymentAuthorized", handlePaymentAuthorized);
      socket.off("tableSessionPaymentRequested", handlePaymentAuthorized);
      socket.off("tableSessionMoved", handlePaymentAuthorized);
      socket.offAny(handleAnySocketEvent);
    };
  }, [status, session?.session_uuid, applySnapshot, refreshSharedOrder, refreshCurrentSession, getMachineSocketQuery]);

  const value = useMemo(() => ({
    loading,
    hasTableSession: Boolean(session && isActiveTableSessionStatus(status)),
    session,
    status,
    error,

    // Fase 3 collaborative state.
    participant,
    participants,
    participantLoading,
    currentInvite,
    inviteLoading,
    inviteError,
    sharedOrder,
    sharedOrderVersion,
    sharedOrderLoading,
    sharedOrderError,
    socketConnected,
    lastMutationError,
    lastItemsSubmittedEvent,

    // Legacy alias - same underlying data as sharedOrder now that the backend returns the same
    // sanitized snapshot from every /table-sessions/public/order verb regardless of path used.
    tableOrder: sharedOrder,
    tableOrderLoading: sharedOrderLoading,
    tableOrderError: sharedOrderError,

    refreshCurrentSession,
    requestTableAccess,
    requestPayment,
    exitTableMode,
    refreshActiveTableOrder,
    submitTableOrder,

    createSessionInvite,
    revokeSessionInvite,
    validateSessionInvite,
    joinSessionWithInvite,
    refreshParticipants,
    refreshSharedOrder,
    addSharedItem,
    updateSharedItem,
    removeSharedItem,
    requestItemDiscount,
    lastDiscountResolvedEvent,
    closedSessionNotice,
    dismissClosedSessionNotice,
    submitMyItems,
    claimPaymentLines,
    confirmPaymentClaim,
    releasePaymentClaim,
    finalizeAccount,
    setInvoiceData,
  }), [
    loading,
    session,
    status,
    error,
    participant,
    participants,
    participantLoading,
    currentInvite,
    inviteLoading,
    inviteError,
    sharedOrder,
    sharedOrderVersion,
    sharedOrderLoading,
    sharedOrderError,
    socketConnected,
    lastMutationError,
    lastItemsSubmittedEvent,
    refreshCurrentSession,
    requestTableAccess,
    requestPayment,
    exitTableMode,
    refreshActiveTableOrder,
    submitTableOrder,
    createSessionInvite,
    revokeSessionInvite,
    validateSessionInvite,
    joinSessionWithInvite,
    refreshParticipants,
    refreshSharedOrder,
    addSharedItem,
    updateSharedItem,
    removeSharedItem,
    requestItemDiscount,
    lastDiscountResolvedEvent,
    closedSessionNotice,
    dismissClosedSessionNotice,
    submitMyItems,
    claimPaymentLines,
    confirmPaymentClaim,
    releasePaymentClaim,
    finalizeAccount,
    setInvoiceData,
  ]);

  return (
    <TableSessionContext.Provider value={value}>
      {children}
    </TableSessionContext.Provider>
  );
};

export const useTableSession = () => {
  const context = useContext(TableSessionContext);
  if (!context) {
    throw new Error("useTableSession must be used within TableSessionProvider");
  }
  return context;
};
